import React, { useState, useEffect } from 'react';
import { ArrowRight, LogIn, FileText, Zap, CheckCircle, Shield } from 'lucide-react';

const LandingPage = () => {
  const [returningUser, setReturningUser] = useState(null);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);

  useEffect(() => {
    // Check if user is already registered
    const stored = localStorage.getItem('nexthesis_user');
    if (stored) {
      try {
        const userData = JSON.parse(stored);
        setReturningUser(userData);

        // If professional, fetch pending requests count
        if (userData.type === 'professional') {
          fetchPendingRequestsCount(userData.email);
        }
      } catch (e) {
        // Invalid data, ignore
      }
    }
  }, []);

  const fetchPendingRequestsCount = async (email) => {
    try {
      const response = await fetch(
        `https://bpupukmduvbzyywbcngj.supabase.co/rest/v1/interview_requests?professional_email=eq.${encodeURIComponent(email)}&select=status`,
        {
          headers: {
            'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJwdXB1a21kdXZienl5d2JjbmdqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjU5OTUzNjAsImV4cCI6MjA4MTU3MTM2MH0._EwWab7_Se-HaTWWl24J-SUBLVVzDjRIYF7q5ShqUzw',
            'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJwdXB1a21kdXZienl5d2JjbmdqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjU5OTUzNjAsImV4cCI6MjA4MTU3MTM2MH0._EwWab7_Se-HaTWWl24J-SUBLVVzDjRIYF7q5ShqUzw'
          }
        }
      );

      const data = await response.json();
      if (Array.isArray(data)) {
        const pendingCount = data.filter(r => r.status === 'pending' || r.status === 'matched').length;
        setPendingRequestsCount(pendingCount);
      }
    } catch (e) {
      console.error('Error fetching pending requests:', e);
    }
  };

  const handleReturningUserClick = () => {
    if (returningUser) {
      if (returningUser.type === 'student') {
        window.location.href = '/welcome/student';
      } else if (returningUser.type === 'professional') {
        window.location.href = '/welcome/professional';
      }
    }
  };

  const companies = [
    { name: 'Nestlé', logo: 'https://logo.clearbit.com/nestle.com' },
    { name: 'Visa', logo: 'https://logo.clearbit.com/visa.com' },
    { name: 'IBM', logo: 'https://logo.clearbit.com/ibm.com' },
    { name: 'PwC', logo: 'https://logo.clearbit.com/pwc.com' },
    { name: 'AWS', logo: 'https://logo.clearbit.com/aws.amazon.com' },
    { name: 'GEP', logo: 'https://logo.clearbit.com/gep.com' },
    { name: 'Infosys', logo: 'https://logo.clearbit.com/infosys.com' },
    { name: 'Deloitte', logo: 'https://logo.clearbit.com/deloitte.com' },
    { name: 'Accenture', logo: 'https://logo.clearbit.com/accenture.com' }
  ];

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      {/* Animated Background Gradient - Enhanced with warmer tones */}
      <div className="fixed inset-0 bg-gradient-to-br from-indigo-900/20 via-purple-900/20 to-pink-900/10 pointer-events-none"></div>
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(79,70,229,0.15),transparent_70%)] pointer-events-none"></div>
      <div className="fixed top-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="fixed bottom-1/4 left-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none"></div>

      <style>{`
        @keyframes scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-scroll {
          animation: scroll 15s linear infinite;
        }
        @media (min-width: 640px) {
          .animate-scroll {
            animation: scroll 25s linear infinite;
          }
        }
        .animate-scroll:hover {
          animation-play-state: paused;
        }
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.3); }
          50% { box-shadow: 0 0 30px rgba(59, 130, 246, 0.5); }
        }
        .pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }
      `}</style>

      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-black/50 backdrop-blur-xl border-b border-white/10 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between">
          <a href="/" className="flex items-center gap-1.5 sm:gap-2 hover:opacity-80 transition-opacity flex-shrink-0">
            <img src="/logo.png" alt="NexThesis" className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 flex-shrink-0" />
            <span className="text-base sm:text-lg md:text-xl font-bold tracking-wide whitespace-nowrap">NexThesis</span>
          </a>
          <div className="flex gap-2 sm:gap-4 items-center">
            <a href="#why" className="hidden sm:inline text-gray-400 hover:text-white transition-colors text-sm font-medium">
              Why NexThesis
            </a>
            <a href="/pricing" className="hidden sm:inline text-gray-400 hover:text-white transition-colors text-sm font-medium">
              Pricing
            </a>
            {returningUser && (
              <button
                onClick={handleReturningUserClick}
                className="px-4 sm:px-5 py-2 sm:py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 border border-blue-500/50 rounded-lg font-semibold text-xs sm:text-sm transition-all flex items-center gap-2 shadow-lg shadow-blue-500/30 relative"
              >
                <LogIn className="w-4 h-4" />
                <span>Welcome Back</span>
                {pendingRequestsCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
                    {pendingRequestsCount}
                  </span>
                )}
              </button>
            )}
            <a href="/browse" className="px-3 sm:px-5 py-2 sm:py-2.5 bg-blue-600 hover:bg-blue-500 rounded-lg font-semibold text-xs sm:text-sm transition-all whitespace-nowrap">
              Browse Experts
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section - Centered and Welcoming */}
      <div className="relative pt-32 sm:pt-40 md:pt-48 pb-16 sm:pb-24 px-4 sm:px-6 min-h-[80vh] flex items-center">
        <div className="max-w-6xl mx-auto text-center w-full">
          {/* Main Headline */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 sm:mb-8 leading-tight px-2" style={{ letterSpacing: '0.02em' }}>
            Expert Interviews for
            <br />
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-blue-500 bg-clip-text text-transparent">
              Your Thesis Research
            </span>
          </h1>

          <p className="text-lg sm:text-xl md:text-2xl text-gray-400 mb-8 sm:mb-10 max-w-3xl mx-auto leading-relaxed font-medium px-2">
            Connect with verified professionals from leading companies
          </p>
          <p className="text-base sm:text-lg md:text-xl text-gray-500 mb-8 sm:mb-10 max-w-2xl mx-auto px-2 -mt-4 sm:-mt-6">
            Structured 30-minute sessions to elevate your academic research
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col items-center gap-4 sm:gap-5">
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto px-4 sm:px-0">
              <a href="/register/student" className="group px-8 sm:px-12 py-4 sm:py-5 bg-blue-600 hover:bg-blue-500 rounded-xl transition-all font-bold text-lg sm:text-xl shadow-lg shadow-blue-500/20 flex items-center justify-center gap-2 pulse-glow" style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1)' }}>
                I'm a Student
                <ArrowRight className="w-5 sm:w-6 h-5 sm:h-6 group-hover:translate-x-1 transition-transform" />
              </a>
              <a href="/register/professional" className="px-8 sm:px-12 py-4 sm:py-5 bg-purple-600 hover:bg-purple-500 rounded-xl transition-all font-bold text-lg sm:text-xl shadow-lg shadow-purple-500/20 flex items-center justify-center gap-2" style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1)' }}>
                I'm a Professional
                <ArrowRight className="w-5 sm:w-6 h-5 sm:h-6 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
            <div className="text-gray-500 text-sm sm:text-base text-center px-4">
              No subscription. Pay per interview. Start with one session.
            </div>
            {!returningUser && (
              <div className="flex flex-col items-center gap-3 mt-2">
                <a href="/login" className="px-8 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all font-medium text-sm flex items-center gap-2 text-gray-300 hover:text-white">
                  <LogIn className="w-4 h-4" />
                  Already registered?
                </a>
                <a href="/browse" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">
                  Or browse experts without signing up →
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Company Logos Carousel - Restored */}
      <div className="relative py-12 sm:py-20 px-4 sm:px-6 bg-gradient-to-b from-transparent via-white/5 to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 bg-gradient-to-r from-white via-blue-200 to-purple-200 bg-clip-text text-transparent px-4">
              Our experts have shaped strategy at:
            </h2>
          </div>

          {/* Animated Company Carousel - Larger Logos */}
          <div className="relative overflow-hidden py-8 sm:py-12">
            <div className="flex animate-scroll gap-16 sm:gap-24 items-center">
              {[...companies, ...companies].map((company, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-center flex-shrink-0"
                >
                  <img
                    src={company.logo}
                    alt={company.name}
                    className="h-12 sm:h-16 md:h-20 w-auto object-contain opacity-50 hover:opacity-100 transition-opacity"
                    style={{ filter: 'brightness(0) invert(1)' }}
                    onError={(e) => {
                      e.target.parentElement.innerHTML = `<span class="text-2xl sm:text-3xl font-bold text-white/70 whitespace-nowrap">${company.name}</span>`;
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div id="how" className="relative py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 px-4">
              How It Works —
              <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent"> Simple as 1-2-3</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-12">
            {[
              {
                num: '1',
                title: 'Browse & Request',
                desc: 'Find experts that match your thesis topic. Pick a time slot that works for you. Hit request and you\'re all set!'
              },
              {
                num: '2',
                title: 'Get Confirmed',
                desc: 'Receive confirmation before you know it. Your expert is ready to share their insights with you.'
              },
              {
                num: '3',
                title: 'Rinse & Repeat',
                desc: 'Had a great session? Book another expert to get diverse perspectives and strengthen your research.'
              }
            ].map((step, idx) => (
              <div key={idx} className="relative group">
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 sm:p-8 hover:bg-white/10 hover:border-blue-400/30 transition-all h-full">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-2xl font-bold flex-shrink-0">
                      {step.num}
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold">{step.title}</h3>
                  </div>
                  <p className="text-gray-400 leading-relaxed text-sm sm:text-base">{step.desc}</p>
                </div>
                {idx < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-blue-500 to-transparent"></div>
                )}
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* LinkedIn Comparison Section */}
      <div id="why" className="relative py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 px-4">
              Why NexThesis,
              <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent"> Not LinkedIn?</span>
            </h2>
          </div>

          {/* Comparison Table - Condensed to Top 4 */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl sm:rounded-3xl p-6 sm:p-10 mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 max-w-3xl mx-auto">
              {/* LinkedIn Column */}
              <div className="text-center md:text-left">
                <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-red-400/80">LinkedIn Cold Outreach</h3>
                <div className="space-y-4 sm:space-y-5">
                  <div className="flex items-center md:items-start gap-3 justify-center md:justify-start">
                    <span className="text-red-400 text-lg sm:text-xl">❌</span>
                    <span className="text-gray-300 text-sm sm:text-base">Poor response rate</span>
                  </div>
                  <div className="flex items-center md:items-start gap-3 justify-center md:justify-start">
                    <span className="text-red-400 text-lg sm:text-xl">❌</span>
                    <span className="text-gray-300 text-sm sm:text-base">Weeks of back-and-forth</span>
                  </div>
                  <div className="flex items-center md:items-start gap-3 justify-center md:justify-start">
                    <span className="text-red-400 text-lg sm:text-xl">❌</span>
                    <span className="text-gray-300 text-sm sm:text-base">Frequent no-shows</span>
                  </div>
                  <div className="flex items-center md:items-start gap-3 justify-center md:justify-start">
                    <span className="text-red-400 text-lg sm:text-xl">❌</span>
                    <span className="text-gray-300 text-sm sm:text-base">Free (but costs 40+ hours)</span>
                  </div>
                </div>
              </div>

              {/* NexThesis Column */}
              <div className="text-center md:text-left">
                <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-green-400">NexThesis</h3>
                <div className="space-y-4 sm:space-y-5">
                  <div className="flex items-center md:items-start gap-3 justify-center md:justify-start">
                    <span className="text-green-400 text-lg sm:text-xl">✅</span>
                    <span className="text-white font-medium text-sm sm:text-base">Guaranteed responses</span>
                  </div>
                  <div className="flex items-center md:items-start gap-3 justify-center md:justify-start">
                    <span className="text-green-400 text-lg sm:text-xl">✅</span>
                    <span className="text-white font-medium text-sm sm:text-base">Book in under 5 minutes</span>
                  </div>
                  <div className="flex items-center md:items-start gap-3 justify-center md:justify-start">
                    <span className="text-green-400 text-lg sm:text-xl">✅</span>
                    <span className="text-white font-medium text-sm sm:text-base">Show-up guaranteed</span>
                  </div>
                  <div className="flex items-center md:items-start gap-3 justify-center md:justify-start">
                    <span className="text-green-400 text-lg sm:text-xl">✅</span>
                    <span className="text-white font-medium text-sm sm:text-base">Paid (but saves you weeks)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section - Professional Icons */}
      <div className="relative py-16 sm:py-24 px-4 sm:px-6">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12 sm:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 px-4">
              Research
              <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent"> Like You Mean It</span>
            </h2>
            <p className="text-gray-400 text-base sm:text-lg md:text-xl max-w-2xl mx-auto px-4">
              Stop guessing. Start interviewing. Connect with industry experts who bring real-world insights to your academic work.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-12 sm:mb-16">
            {/* Benefit Cards - Professional Icons */}
            <div className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 hover:border-blue-400/50 transition-all cursor-pointer">
              <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-white">5 Minutes to Scheduled</h3>
              <p className="text-gray-400 leading-relaxed text-sm">
                Browse. Click. Confirm. Your expert interview is booked—no LinkedIn cold-pitching required.
              </p>
            </div>

            <div className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 hover:border-purple-400/50 transition-all cursor-pointer">
              <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-white">Fortune 500 Credentials</h3>
              <p className="text-gray-400 leading-relaxed text-sm">
                Interview professionals from leading companies like Nestlé, Visa, and more. Not random LinkedIn connections.
              </p>
            </div>

            <div className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 hover:border-blue-400/50 transition-all cursor-pointer">
              <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center mb-4">
                <CheckCircle className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-white">They Show Up. Guaranteed.</h3>
              <p className="text-gray-400 leading-relaxed text-sm">
                Prepaid commitment system. No ghosting. No rescheduling chaos. Just reliable expertise.
              </p>
            </div>

            <div className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 hover:border-purple-400/50 transition-all cursor-pointer">
              <div className="w-12 h-12 bg-pink-500/10 rounded-xl flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-pink-400" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-white">Research-Ready Sessions</h3>
              <p className="text-gray-400 leading-relaxed text-sm">
                30 focused minutes. Recordable. Designed for academic rigor, not casual chats.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12 px-6 bg-black/50 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src="/logo.png" alt="NexThesis" className="w-6 h-6" />
            <span className="text-xl font-bold">NexThesis</span>
          </div>
          <p className="text-gray-500 text-sm">© 2026 NexThesis. Connecting students with industry experts.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
